import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Link,
  Alert,
  AlertTitle,
  SxProps,
} from '@mui/material';
import { FC, useEffect, useState, useRef } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';

import { Job } from '@libs/models';
import { DeepPartial, toHuman, toHumanSalary } from '@libs/utils';
import { RichTextEditor } from '@carcareers/components';

interface JobPreviewProps {
  data: DeepPartial<Job>;
  descriptionFillHeight?: boolean;
  linkToRooftop?: boolean;
  inactive?: boolean;
  sx?: SxProps;
}

export const JobPreview: FC<JobPreviewProps> = ({
  data,
  sx,
  descriptionFillHeight = false,
  linkToRooftop = false,
  inactive = false,
}) => {
  const [descriptionKey, setDescriptionKey] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [descriptionHeight, setDescriptionHeight] = useState<string>('auto');

  const isConfidential = data?.rooftop?.isConfidential;

  // Update key when description changes to force remount
  useEffect(() => {
    setDescriptionKey((prev) => prev + 1);
  }, [data?.description]);

  // Calculate available height for description when descriptionFillHeight is true
  useEffect(() => {
    if (descriptionFillHeight && containerRef.current) {
      const calculateHeight = () => {
        const containerHeight = containerRef.current?.clientHeight || 0;
        const headerHeight = 300; // Approximate height of elements above the description
        const availableHeight = Math.max(300, containerHeight - headerHeight);
        setDescriptionHeight(`${availableHeight}px`);
      };

      calculateHeight();
      window.addEventListener('resize', calculateHeight);
      return () => window.removeEventListener('resize', calculateHeight);
    } else {
      setDescriptionHeight('auto');
    }
  }, [descriptionFillHeight]);

  // Render employer section based on confidential status
  const renderEmployerSection = () => {
    if (isConfidential) {
      return (
        <ListItemText
          primary={<Typography variant="h4">Employer</Typography>}
          secondary={
            <Typography variant="body1" color="text.secondary">
              Confidential
            </Typography>
          }
        />
      );
    }

    if (linkToRooftop && data?.rooftop?.id) {
      return (
        <ListItemText
          primary={<Typography variant="h4">Employer</Typography>}
          secondary={
            <Link
              href={`/rooftop/${data?.rooftop?.id}`}
              sx={{
                display: 'flex',
                textDecoration: 'none',
                width: 'fit-content',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="body1"
                sx={{
                  '&:hover': { backgroundColor: 'grey.100', borderRadius: '4px', px: 1, ml: -1 },
                  width: 'fit-content',
                }}
              >
                {data?.rooftop?.name}
              </Typography>
            </Link>
          }
        />
      );
    }

    return (
      <ListItemText
        primary={<Typography variant="h4">Employer</Typography>}
        secondary={data?.rooftop?.name || 'None'}
      />
    );
  };

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', height: '100%', ...sx }} ref={containerRef}>
      <Box sx={{ p: 2 }}>
        {inactive && (
          <Alert severity="warning" sx={{ borderRadius: 4, mb: 2 }}>
            <AlertTitle>This job posting has been closed.</AlertTitle>
            Applications for this position are now closed. We encourage you to explore other opportunities on our
            careers page.
          </Alert>
        )}
        <Typography variant="h2" sx={{ mb: 3 }}>
          {data?.title}
        </Typography>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            {renderEmployerSection()}
          </ListItem>
          <ListItem disablePadding>
            <ListItemIcon>
              <LocationOnIcon />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="h4">Location</Typography>}
              secondary={data?.rooftop?.location || 'None'}
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemIcon>
              <AttachMoneyIcon />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="h4">Salary</Typography>}
              secondary={toHumanSalary(data?.compensationMin, data?.compensationMax, data?.compensationType)}
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemIcon>
              <CardGiftcardIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Benefits
                </Typography>
              }
              secondary={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data?.benefits?.length === 0 || (!data?.benefits && <Typography variant="body1">None</Typography>)}
                  {data?.benefits?.map((benefit, index) => (
                    <Chip key={index} label={toHuman('camel', benefit, true)} size="small" />
                  ))}
                </Box>
              }
            />
          </ListItem>
        </List>
        <Box
          sx={{
            '& > *': { my: 2 },
            height: descriptionFillHeight ? descriptionHeight : 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!data?.description && <Typography variant="body1">No description provided</Typography>}
          {data?.description && (
            <RichTextEditor
              key={`description-${descriptionKey}`}
              data={data?.description}
              readOnly={true}
              fillHeight={descriptionFillHeight}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
